import { S } from '@mobily/ts-belt';
import { Box, CloudinaryVideo, Inline, ResponsiveSection, Stack, useAtBreakpoint } from '@stitch-fix/mode-react';
import { m } from 'framer-motion';
import classNames from 'classnames';
import type { ContentStackJSONRichText } from '../../utils/requests/contentstack/contentStackZodSchema';
import { type CloudinaryImageFields, CloudinaryImageWrapper } from '../CloudinaryImageWrapper';
import { JsonRteRenderer } from '../JsonRteRenderer';
import { ModuleFooter, type ModuleFooterProps } from '../ModuleFooter';
import { ModuleTitle, type ModuleTitleProps } from '../ModuleTitle';
import styles from './styles.module.scss';
import { isEmpty } from '../JsonRteRenderer/JsonRteRenderer';
import { MODE_MIN_WIDTH } from '../../utils/values/mediaQueries';
import { parseCloudinaryVideoUrl } from '../../utils/parseCloudinaryUrl/parseCloudinaryUrl';
import { ModularComponentProps } from '../ModularPageWrapper/ModularPageWrapper';
import { ModuleWrapper } from '../ModuleWrapper/ModuleWrapper';

/**
 * DisplayPanel component with framer-motion animations for media scaling.
 *
 * Note: This component expects to be rendered within a MotionConfig provider
 * (e.g., ModularPageWrapper) to ensure animations respect user accessibility
 * preferences for reduced motion.
 */

type DisplayPanelTextColor = 'blue-45' | 'mint-60' | 'white' | 'forest-60' | 'grapefruit-60' | 'citrus-60';
export type DisplayPanelProps = {
  moduleTitle: ModuleTitleProps;
  moduleFooter: ModuleFooterProps;
  media: CloudinaryImageFields;
  text: ContentStackJSONRichText;
  attribution: ContentStackJSONRichText;
  avatarImage?: CloudinaryImageFields;
  contentOrder?: 'textFirst' | 'mediaFirst';
  backgroundColor: 'gray-99' | 'white';
  textColor?: DisplayPanelTextColor;
  majorTitleTextColor?: DisplayPanelTextColor;
  minorTitleTextColor?: DisplayPanelTextColor;
  removeMediaZoom?: boolean;
  textCenterAligned?: boolean;
};
const mediaSizes = {
  // Max width of sm viewports is 560:
  sm: 560,
  // `max-width` of container is 644px (up until 900px viewport):
  md: 650,
  // Two column layout, ResponsiveSection max-width is 1084 - 1084/2 = 542
  // We re-use the sizing at the `sm` breakpoint:
  lg: 560
};
export const DisplayPanel = ({
  moduleTitle,
  moduleFooter,
  media,
  text,
  avatarImage,
  attribution,
  contentOrder = 'mediaFirst',
  backgroundColor,
  verticalSpacing,
  textColor,
  majorTitleTextColor,
  minorTitleTextColor,
  removeMediaZoom = false,
  textCenterAligned = false
}: DisplayPanelProps & ModularComponentProps) => {
  const displayEmbeddedModuleWrapper = useAtBreakpoint(900);
  const shouldRenderModuleTitle = !isEmpty(moduleTitle.title?.children) || !isEmpty(moduleTitle.major?.children);
  const shouldRenderModuleFooter = S.isNotEmpty(moduleFooter.link.href) && S.isNotEmpty(moduleFooter.link.title);
  const shouldRenderAvatar = avatarImage?.src && !isEmpty(attribution?.children);
  const isVideo = media.src.includes('.mp4');
  const shouldDisplayEmbeddedTitle = displayEmbeddedModuleWrapper && shouldRenderModuleTitle;
  const shouldDisplayText = !isEmpty(text?.children);
  const shouldDisplayEmbeddedFooter = displayEmbeddedModuleWrapper && shouldRenderModuleFooter;
  const shouldDisplayContent = shouldDisplayEmbeddedTitle || shouldDisplayText || shouldRenderAvatar || shouldDisplayEmbeddedFooter;
  const renderMedia = () => {
    if (isVideo) {
      const {
        baseUrl,
        mediaPath
      } = parseCloudinaryVideoUrl(media.src);
      return <CloudinaryVideo key={media.src} baseUrl={baseUrl} mediaPath={mediaPath} intent="decorative" sizes={mediaSizes} />;
    }
    return <CloudinaryImageWrapper alt={media.alt} src={media.src} sources={{
      ...mediaSizes
    }} sizes={[{
      minWidth: 0,
      fluidWidth: 100
    }, {
      minWidth: MODE_MIN_WIDTH.md,
      fluidWidth: 50
    }]} imageProps={{
      loading: 'lazy'
    }} cloudinaryAsset={media.cloudinaryAsset} />;
  };
  return <Box as="section" bgColor={backgroundColor} pt={backgroundColor === 'white' ? 0 : 3} pb={{
    sm: !moduleFooter.hideOnMobile && shouldRenderModuleFooter ? 3 : 0,
    lg: 3
  }} mt={verticalSpacing}>
      <ModuleWrapper alignment="center" gutter="standard" textColor={textColor} majorTitleTextColor={majorTitleTextColor} minorTitleTextColor={minorTitleTextColor} moduleTitle={shouldDisplayEmbeddedTitle ? undefined : moduleTitle} moduleFooter={shouldDisplayEmbeddedFooter ? undefined : moduleFooter}>
        <ResponsiveSection width="xlarge" gutter="none">
          <Box className={styles.container}>
            <Box className={classNames(styles.media, {
            [styles.videoContentOverlap]: isVideo && shouldDisplayContent,
            [styles.textFirst]: contentOrder === 'textFirst'
          })}>
              {removeMediaZoom ? renderMedia() : <m.div className={isVideo ? styles.videoZoom : undefined} initial={isVideo ? {
              '--media-scale': 1
            } : {
              scale: 1
            }} viewport={{
              once: true,
              amount: 0.5
            }} whileInView={isVideo ? {
              '--media-scale': 1.2
            } : {
              scale: 1.2
            }} style={{
              transformOrigin: 'top center'
            }} transition={{
              duration: 0.8,
              ease: 'easeInOut'
            }}>
                  {renderMedia()}
                </m.div>}
            </Box>
            {shouldDisplayContent && <Box className={classNames(styles.content, {
            [styles.textFirst]: contentOrder === 'textFirst',
            [styles.centerText]: textCenterAligned
          })}>
                {shouldDisplayEmbeddedTitle && <Box mb={2.5}>
                    <ModuleTitle hAlign={textCenterAligned ? 'center' : 'start'} render={items => {
                return <Stack hAlign={textCenterAligned ? 'center' : 'start'} spacing={0.5} data-testid="title-container">
                            {items}
                          </Stack>;
              }} {...moduleTitle} textColor={textColor} majorTitleTextColor={majorTitleTextColor} minorTitleTextColor={minorTitleTextColor} />
                  </Box>}
                {shouldDisplayText && <JsonRteRenderer data={text} options={{
              color: textColor || 'blue-45',
              textSizeMap: 'display',
              textProps: {
                p: {
                  setting: 'body-large'
                }
              }
            }} />}
                {shouldRenderAvatar && <Inline spacing="1rem" mt={2} vAlign="center">
                    {avatarImage.src && <Box className={styles.avatar}>
                        <CloudinaryImageWrapper alt={avatarImage.alt} src={avatarImage.src} sources={{
                  sm: 64
                }} imageProps={{
                  loading: 'lazy'
                }} cloudinaryAsset={avatarImage.cloudinaryAsset} sizes={[{
                  minWidth: 0,
                  staticWidth: 64
                }]} />
                      </Box>}

                    {!isEmpty(attribution?.children) && <JsonRteRenderer data={attribution} options={{
                textSizeMap: 'display',
                textProps: {
                  p: {
                    setting: 'body-large',
                    color: textColor || 'blue-45',
                    spacingBottom: false
                  }
                }
              }} />}
                  </Inline>}

                {shouldDisplayEmbeddedFooter && <ModuleFooter {...moduleFooter} />}
              </Box>}
          </Box>
        </ResponsiveSection>
      </ModuleWrapper>
    </Box>;
};